import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronDown } from "lucide-react";
import Select from "../common/Select";
import type { ReportPeriod } from "./reportTabTypes";

export type PeriodPickerMode = "year" | "month";

interface PeriodPickerProps {
  mode: PeriodPickerMode;
  period: ReportPeriod;
  onChange: (period: ReportPeriod) => void;
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString("en-US", { month: "short" }),
}));

const getYearOptions = (): { value: number; label: string }[] => {
  const current = new Date().getFullYear();
  return [
    { value: current, label: `FY ${current}` },
    { value: current - 1, label: `FY ${current - 1}` },
    { value: current - 2, label: `FY ${current - 2}` },
  ];
};

const getPeriodLabel = (mode: PeriodPickerMode, period: ReportPeriod): string => {
  if (mode === "year") return `FY ${period.year}`;
  const monthName = MONTH_OPTIONS.find((m) => m.value === period.month)?.label ?? "";
  return `${monthName} ${period.year}`;
};

const PeriodPicker: React.FC<PeriodPickerProps> = ({ mode, period, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [menuTop, setMenuTop] = useState(0);
  const [menuRight, setMenuRight] = useState(16);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuTop(rect.bottom + 8);
      setMenuRight(window.innerWidth - rect.right);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  const commit = useCallback(
    (next: ReportPeriod) => {
      onChange(next);
      setIsOpen(false);
    },
    [onChange],
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="flex w-fit items-center gap-2 px-3 py-2 text-label text-[var(--color-text-secondary)] bg-white border border-[var(--color-border-primary)] rounded-md hover:bg-[var(--color-bg-muted)] transition-all duration-200"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Calendar size={16} className="text-[var(--color-text-tertiary)]" />
        <span>{getPeriodLabel(mode, period)}</span>
        <ChevronDown size={14} className="text-[var(--color-text-tertiary)]" />
      </button>

      {mounted &&
        isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed w-52 py-4 px-4 rounded-button border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] shadow-xl z-[1100] animate-in fade-in zoom-in-95 duration-200"
            style={{ top: menuTop, right: menuRight }}
          >
            {mode === "year" ? (
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-widest mb-2">
                    Year
                  </span>
                  <Select
                    className="w-full"
                    size="md"
                    clearable={false}
                    value={period.year}
                    options={getYearOptions()}
                    onChange={(value) => {
                      if (Array.isArray(value)) return;
                      commit({ ...period, year: Number(value), periodType: undefined });
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-widest mb-2">
                    Month
                  </span>
                  <Select
                    className="w-full"
                    size="md"
                    clearable={false}
                    value={period.month}
                    options={MONTH_OPTIONS}
                    onChange={(value) => {
                      if (Array.isArray(value)) return;
                      commit({ ...period, month: Number(value) });
                    }}
                  />
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-widest mb-2">
                    Year
                  </span>
                  <Select
                    className="w-full"
                    size="md"
                    clearable={false}
                    value={period.year}
                    options={getYearOptions()}
                    onChange={(value) => {
                      if (Array.isArray(value)) return;
                      commit({ ...period, year: Number(value), periodType: undefined });
                    }}
                  />
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default PeriodPicker;